import { useCallback } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import { FileUpload, GoodieFile, PRESETS } from '@/features/files'
import { helpers as brandingHelpers } from '@/features/branding'
import ColorPicker from '@/components/ui/ColorPicker'
import Checkbox from '@/components/ui/Checkbox'
import ComboBox from '@/components/ui/ComboBox'
import Input from '@/components/ui/Input'
import { useCreateOrganization } from '../hooks/useCreateOrganization'
import { slugify } from '../utils/slugify'
import { allLocalesAsOptions } from '@/features/translations'

type FormData = {
  name: string
  slug: string
  brandingColor: string
  file: GoodieFile
  currency: GraphQL.Currency
  timezone: string
  acceptedContract: boolean
  defaultLocale: GraphQL.AppLocale
}

export default function CreateOrganization() {
  const { t } = useLingui()
  const { create, errors } = useCreateOrganization()

  const submit = useCallback(
    async (data: FormData) => {
      await create(data)
    },
    [create]
  )

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-center text-heading1">
        <Trans>Create an App for your Hotel</Trans>
      </h1>
      <Form.Root<FormData>
        onSubmit={submit}
        serverErrors={errors}
        defaultValues={{
          name: '',
          slug: '',
          brandingColor: '#000000',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          acceptedContract: false
        }}
      >
        <div className="flex w-full flex-col gap-6 items-stretch">
          <Form.Field
            name="name"
            label={`${t`Hotel Name`}`}
            rules={{
              maxLength: {
                value: 255,
                message: t`Can't be longer than 255 characters.`
              },
              validate: {
                required: Form.Validations.requiredWithTrimValidation(
                  t`This field is required`
                )
              }
            }}
          >
            {({ setValue, renderProps: { onChange, ...props } }) => (
              <Input
                type="text"
                onChange={event => {
                  const value = event.target.value

                  onChange(value)
                  setValue('slug', slugify(value))
                }}
                autoComplete="organization"
                placeholder={t({
                  comment:
                    'Placeholder for the Hotel Name Input when creating a new Hotel',
                  message: 'e.g.: Aure'
                })}
                {...props}
              />
            )}
          </Form.Field>
          <div className="flex flex-col">
            <Form.Field
              name="slug"
              label={t`Subdomain`}
              description={t`The chosen subdomain has to be unique for your GuestGoodie App, don't use domains like "app".`}
              rules={{
                maxLength: {
                  value: 255,
                  message: t`Can't be longer than 255 characters.`
                },
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ setValue, renderProps: { onChange, ...props } }) => (
                <>
                  <Input
                    type="text"
                    onChange={event => {
                      const value = event.target.value

                      onChange(value)
                      setValue('slug', slugify(value))
                    }}
                    placeholder={t({
                      comment:
                        'Placeholder for the Subdomain Input when creating a new Hotel',
                      message: 'e.g.: aure'
                    })}
                    {...props}
                  />
                </>
              )}
            </Form.Field>
            <Form.Data<FormData>>
              {({ slug }) => {
                if (!slug) return null

                return (
                  <span className="pl-2 pt-2 text-text-light text-regular w-full">
                    <span>
                      <strong>
                        <Trans>Will look like this</Trans>:
                      </strong>{' '}
                      {`${slug}.guestgoodie.com`}
                    </span>
                  </span>
                )
              }}
            </Form.Data>
          </div>
          <Form.Field
            name="currency"
            label={`${t`Currency`}`}
            description={t`Choose the Currency that is shown in the GuestApp.`}
            rules={{
              required: t`This field is required`
            }}
          >
            {({ renderProps: props }) => (
              <ComboBox
                options={brandingHelpers.currencies}
                placeholder={t`Choose Currency`}
                {...props}
              />
            )}
          </Form.Field>
          <Form.Field
            name="timezone"
            label={`${t`Timezone`}`}
            description={t`The timezone of the Hotel, we show the calendar/orders/events etc. in this Timezone.`}
            rules={{
              required: t`This field is required`
            }}
          >
            {({ renderProps: props }) => (
              <ComboBox
                options={brandingHelpers.timezones}
                placeholder={t`Choose a Timezone`}
                {...props}
              />
            )}
          </Form.Field>
          <Form.Field
            name="defaultLocale"
            size="small"
            label={t`Default Language`}
            description={t`The default language that is used by your App. You can add extra supported languages in your Branding settings.`}
            rules={{
              required: t`This field is required`
            }}
          >
            {({ renderProps: props }) => (
              <ComboBox
                options={allLocalesAsOptions}
                placeholder={t`Choose a Language`}
                {...props}
              />
            )}
          </Form.Field>
          <Form.Field
            name="brandingColor"
            label={`${t`Main Brand Color`}`}
            description={t`The main branding color, this could be your logo color. We recommend a not to light color. This can be changed at any time in the backoffice.`}
            rules={{
              required: t`This field is required`
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <ColorPicker
                onChange={value => onChange(value.target.value)}
                {...props}
              />
            )}
          </Form.Field>
          <Form.Field
            name="file"
            label={`${t`Logo`}`}
            description={t`Your Logo, you have to crop it to a square after selecting it, so that we can use it in browsers as Icon.`}
            rules={{
              required: t`This field is required`
            }}
          >
            {({ renderProps: props }) => (
              <FileUpload
                accept="image/*"
                placeholder={t`Choose a Logo`}
                objectFit="contain"
                crop={{
                  aspectRatio: 1,
                  shape: 'rect',
                  allowPadding: true
                }}
                imageResize={PRESETS.LOGO}
                {...props}
                className="aspect-square self-center"
              />
            )}
          </Form.Field>
          <Form.Field
            name="acceptedContract"
            rules={{ required: t`This field is required` }}
          >
            {({ renderProps: { onChange, value, ...props } }) => (
              <Checkbox
                checked={value}
                onChange={() => onChange(!value)}
                {...props}
              >
                <div className="inline">
                  <Trans>
                    I agree to GuestGoodies{' '}
                    <a
                      className="text-secondary underline"
                      href="/saas"
                      target="_blank"
                    >
                      SaaS Contract and Data Processing Agreement
                    </a>
                    .
                  </Trans>
                </div>
              </Checkbox>
            )}
          </Form.Field>
          <Form.Error name="root.serverError" />
          <div className="flex flex-row justify-end">
            <Form.Submit>
              <Trans>Create</Trans>
            </Form.Submit>
          </div>
        </div>
      </Form.Root>
    </div>
  )
}
